import { useState, useCallback, useRef, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useRealtimeSubscription } from "@/hooks/useRealtimeSubscription";
import { useToast } from "@/hooks/use-toast";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Notification } from "@/components/dashboard/NotificationsPanel";
import { triggerFireworks } from "@/lib/confetti";
import { playNotificationSound } from "@/utils/notificationSound";
import { useDashboardData } from "@/contexts/DashboardDataContext";

interface NotificationPreferences {
  new_feedback_enabled: boolean;
  sentiment_alerts_enabled: boolean;
  milestone_alerts_enabled: boolean;
  sound_notifications_enabled: boolean;
  sound_notification_volume: number;
}

const MAX_NOTIFICATIONS = 50;

export const useNotifications = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [notifications, setNotifications] = useState<Notification[]>(() => {
    // Load notifications from localStorage on mount
    const saved = localStorage.getItem("notifications");
    const parsed = saved ? JSON.parse(saved) : [];
    // Ensure we don't exceed max on load
    return parsed.slice(0, MAX_NOTIFICATIONS);
  });
  const celebratedMilestones = useRef<Set<number>>(
    new Set(JSON.parse(localStorage.getItem("celebrated-milestones") || "[]"))
  );

  // Fetch notification preferences
  const { data: preferences } = useQuery({
    queryKey: ["notification-preferences", user?.id],
    queryFn: async () => {
      if (!user?.id) return null;
      const { data, error } = await supabase
        .from("notification_preferences")
        .select("new_feedback_enabled, sentiment_alerts_enabled, milestone_alerts_enabled, sound_notifications_enabled, sound_notification_volume")
        .eq("user_id", user.id)
        .single();

      if (error) {
        // Return defaults if no preferences found
        return {
          new_feedback_enabled: true,
          sentiment_alerts_enabled: true,
          milestone_alerts_enabled: true,
          sound_notifications_enabled: true,
          sound_notification_volume: 30,
        };
      }
      return data as NotificationPreferences;
    },
    enabled: !!user,
  });

  // Save notifications to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem("notifications", JSON.stringify(notifications));
  }, [notifications]);

  // Use shared queries from context instead of duplicating
  const { pages, dashboardData } = useDashboardData();
  
  const dashboardStats = dashboardData?.stats as any;

  // Handle new notification from realtime subscription with preference filtering
  const handleNewNotification = useCallback((notification: Notification) => {
    // Check if notification type is enabled in preferences
    if (preferences) {
      if (notification.type === "new_feedback" && !preferences.new_feedback_enabled) return;
      if (notification.type === "sentiment_alert" && !preferences.sentiment_alerts_enabled) return;
      if (notification.type === "milestone" && !preferences.milestone_alerts_enabled) return;
    }
    
    setNotifications(prev => {
      const updated = [notification, ...prev];
      // Keep only the most recent MAX_NOTIFICATIONS, prioritizing unread
      if (updated.length > MAX_NOTIFICATIONS) {
        const unread = updated.filter(n => !n.read);
        const read = updated.filter(n => n.read);
        return [...unread, ...read].slice(0, MAX_NOTIFICATIONS);
      }
      return updated;
    });
    
    // Play sound notification for new feedback if enabled
    if (preferences?.sound_notifications_enabled && notification.type === "new_feedback") {
      playNotificationSound(preferences.sound_notification_volume);
    }
  }, [preferences]);

  // Set up realtime subscription
  useRealtimeSubscription({
    user,
    pages,
    onNewNotification: handleNewNotification,
  });

  // Check for milestones
  useEffect(() => {
    if (!dashboardStats || !preferences) return;

    // Only create milestone notifications if enabled in preferences
    if (!preferences.milestone_alerts_enabled) return;

    const milestones = [10, 50, 100, 500, 1000];
    const total = dashboardStats.totalSubmissions;

    if (milestones.includes(total) && !celebratedMilestones.current.has(total)) {
      celebratedMilestones.current.add(total);
      // Persist to localStorage so celebration only happens once
      localStorage.setItem("celebrated-milestones", JSON.stringify([...celebratedMilestones.current]));
      
      const milestoneNotification: Notification = {
        id: `milestone-${total}`,
        type: "milestone",
        title: "Milestone Reached!",
        message: `You've collected ${total} pieces of feedback! 🎉`,
        timestamp: new Date().toISOString(),
        read: false,
      };

      setNotifications(prev => {
        // Don't add duplicate milestones
        if (prev.some(n => n.id === milestoneNotification.id)) return prev;
        return [milestoneNotification, ...prev];
      });

      // Trigger celebratory fireworks for milestones!
      setTimeout(() => {
        triggerFireworks();
        toast({
          title: `🎉 ${total} Submissions Milestone!`,
          description: `Congratulations! You've collected ${total} pieces of feedback. Keep up the great work!`,
          duration: 6000,
        });
      }, 300);
    }
  }, [dashboardStats?.totalSubmissions, preferences, toast]);

  const handleMarkAsRead = useCallback((id: string) => {
    setNotifications(prev =>
      prev.map(n => (n.id === id ? { ...n, read: true } : n))
    );
  }, []);

  const handleMarkAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
  }, []);

  const handleDismissNotification = useCallback((id: string) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  const handleClearAll = useCallback(() => {
    setNotifications([]);
  }, []);

  const handleToggleSound = useCallback(async () => {
    if (!user?.id || !preferences) return;
    
    const newValue = !preferences.sound_notifications_enabled;
    
    try {
      const { error } = await supabase
        .from("notification_preferences")
        .update({ sound_notifications_enabled: newValue })
        .eq("user_id", user.id);
      
      if (error) throw error;
      
      // Invalidate query to refetch preferences
      queryClient.invalidateQueries({ queryKey: ["notification-preferences", user.id] });
      
      toast({
        title: "Sound notifications",
        description: newValue 
          ? "Sound notifications enabled" 
          : "Sound notifications disabled",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update sound preferences",
        variant: "destructive",
      });
    }
  }, [user?.id, preferences, toast, queryClient]);

  const unreadCount = notifications.filter(n => !n.read).length;

  return {
    notifications,
    unreadCount,
    handleMarkAsRead,
    handleMarkAllAsRead,
    handleDismissNotification,
    handleClearAll,
    soundEnabled: preferences?.sound_notifications_enabled ?? true,
    handleToggleSound,
  };
};
